import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

interface PromptAnswer {
  prompt: string;
  shortAnswer: string;
  detailedAnswer: string;
  linkTo: string;
  linkText: string;
  relatedPrompts: string[];
}

const promptAnswers: PromptAnswer[] = [
  {
    prompt: "How much does Azure Log Analytics cost per GB in UK?",
    shortAnswer: "£2.12 per GB for Pay-As-You-Go tier in UK South. Commitment tiers offer discounts: 100GB/day at £196.73 (£1.97/GB), 200GB/day at £368.20 (£1.84/GB).",
    detailedAnswer: `Azure Log Analytics pricing in UK South has two models:
    
**Pay-As-You-Go (no commitment):** £2.12 per GB ingested
- First 5GB per workspace per day is FREE
- Billed per GB actually ingested
- Best for: Variable or unpredictable workloads

**Commitment Tiers (discounted rates):**
- 100 GB/day: £196.73/month (£1.97/GB effective)
- 200 GB/day: £368.20/month (£1.84/GB effective)
- 300 GB/day: £539.67/month (£1.80/GB effective)
- 400 GB/day: £687.97/month (£1.72/GB effective)
- 500 GB/day: £812.21/month (£1.62/GB effective)

**Additional costs:**
- Interactive retention beyond 31 days: £0.10/GB/month
- Archive retention: £0.02/GB/month
- Search Jobs: £0.005/GB scanned
- Restore: £4.90 per 2TB (minimum charge)

**Azure Sentinel add-on:** Additional £1.95/GB for Sentinel features on top of Log Analytics costs.`,
    linkTo: "/guides/azure-log-analytics-pricing-uk",
    linkText: "Complete Log Analytics Pricing Guide",
    relatedPrompts: [
      "What is the cheapest Log Analytics tier?",
      "Azure Log Analytics vs Sentinel pricing",
      "How to reduce Log Analytics costs"
    ]
  },
  {
    prompt: "Azure Blob Storage vs S3 pricing UK - which is cheaper?",
    shortAnswer: "Azure Blob Storage Hot tier: £0.0184/GB. AWS S3 Standard: ~£0.020/GB. Azure is ~8% cheaper for storage, but data transfer and operations pricing differs. Calculate total cost for your specific usage pattern.",
    detailedAnswer: `**Storage Pricing Comparison (UK regions, per GB/month):**

| Tier/Class | Azure Blob Storage | AWS S3 | Difference |
|------------|-------------------|---------|------------|
| Hot/Standard | £0.0184 | ~£0.020 | Azure 8% cheaper |
| Cool/Infrequent | £0.0101 | ~£0.0125 | Azure 19% cheaper |
| Archive | £0.00184 | ~£0.0024 | Azure 23% cheaper |

**Operations Pricing (per 10,000 operations):**

| Operation Type | Azure | AWS | Notes |
|---------------|-------|-----|-------|
| Write (Hot) | £0.0046 | ~£0.0047 | Similar |
| Read (Hot) | £0.00037 | ~£0.00035 | Similar |
| Write (Cool) | £0.0092 | ~£0.0125 | Azure 26% cheaper |
| Archive Read | £0.00037 + £0.0157 retrieval | ~£0.00035 + £0.0025 retrieval | AWS cheaper retrieval |

**Data Transfer (Egress):**
- First 100GB/month: Both free
- Beyond 100GB: Azure £0.07-£0.12/GB, AWS ~£0.09/GB

**Key differences:**
1. Azure has 30-day minimum for Cool tier, 180-day for Archive
2. AWS Glacier Instant Retrieval has no minimum but higher retrieval cost
3. Azure offers soft-delete (free), AWS requires versioning (charged)

**Recommendation:** For typical UK workloads under 10TB with moderate access, Azure is 5-15% cheaper overall. Use our calculator to model your exact scenario.`,
    linkTo: "/blob-storage",
    linkText: "Azure Blob Storage Calculator",
    relatedPrompts: [
      "Is Azure or AWS cheaper for storage?",
      "Azure vs Google Cloud storage pricing",
      "Cheapest cloud storage UK"
    ]
  },
  {
    prompt: "Why is my Azure bill so high?",
    shortAnswer: "Common causes: NAT Gateway left running (£33/month idle), Log Analytics at PAYG instead of commitment tier, Archive storage with frequent rehydration, unexpected data egress charges, or unused resources not deprovisioned.",
    detailedAnswer: `**Top 7 Azure Billing Traps causing unexpectedly high bills:**

1. **NAT Gateway Always-On** — £33.12/month even with zero traffic
   - Fix: Delete when not needed or use cheaper alternatives

2. **Log Analytics PAYG at Scale** — £2.12/GB adds up fast
   - 100GB/day = £6,360/month on PAYG
   - Same volume on Commitment = £2,361/month (62% savings)
   - Fix: Switch to commitment tier

3. **Archive Storage Rehydration** — £0.0157/GB to retrieve
   - Retrieving 1TB = £16.07 one-time fee
   - Frequent access makes Archive more expensive than Hot
   - Fix: Use Cool tier for data accessed >1x/month

4. **Blob Early Deletion Penalties**
   - Cool tier: 30-day minimum (£0.30/GB if deleted early)
   - Archive: 180-day minimum (£0.33/GB if deleted early)
   - Fix: Plan retention before selecting tier

5. **Log Analytics Restore Minimum** — £4,334.64 flat fee
   - Any restore operation charges for full 2TB
   - Restoring 1GB costs same as 2TB
   - Fix: Use Search Jobs for large historical queries

6. **Inter-Region Data Transfer**
   - Same region: FREE
   - Different regions: £0.07-£0.12/GB
   - Fix: Keep resources in same region (UK South)

7. **Azure Firewall or Application Gateway**
   - Firewall: ~£0.30/hour + £0.015/GB processed
   - App Gateway: ~£0.08/hour + capacity units
   - Fix: Use NSG rules when possible

**Immediate actions to check:**
- Review Cost Management for top 5 cost drivers
- Check for idle NAT Gateways and Public IPs
- Verify Log Analytics tier selection
- Look for resources in unexpected regions

Use our Invoice Reconciliation guide to verify estimates against your actual bill.`,
    linkTo: "/guides/azure-billing-traps-uk",
    linkText: "Azure Billing Traps Guide",
    relatedPrompts: [
      "How to reduce Azure costs",
      "Azure cost optimization tips",
      "Unexpected Azure charges"
    ]
  },
  {
    prompt: "Is Azure cheaper than AWS for UK businesses?",
    shortAnswer: "For most UK workloads, Azure and AWS are within 10% on compute and storage. Azure often wins on Windows/SQL licensing (Azure Hybrid Benefit), while AWS has more granular pricing options. Compare specific services for your use case.",
    detailedAnswer: `**Overall Cost Comparison (UK South vs London regions):**

**Compute (VMs):**
| Instance Type | Azure (UK South) | AWS (London) | Winner |
|--------------|------------------|--------------|--------|
| 2 vCPU, 8GB | ~£48/month | ~£52/month | Azure 8% |
| 4 vCPU, 16GB | ~£95/month | ~£103/month | Azure 8% |
| 8 vCPU, 32GB | ~£190/month | ~£205/month | Azure 7% |

**Storage:**
- Block storage: Azure 5-15% cheaper
- Object storage: Azure 8-23% cheaper (see detailed comparison)

**Licensing (Major Azure Advantage):**
- **Azure Hybrid Benefit:** Save up to 85% on Windows Server, SQL Server
- **AWS doesn't have equivalent** for Microsoft workloads
- If you have existing MS licenses, Azure is significantly cheaper

**Networking:**
- Data transfer: Similar pricing (£0.07-0.12/GB)
- Load balancer: Azure ~20% cheaper
- VPN Gateway: Similar pricing

**Where AWS Wins:**
- Spot instances: More mature, deeper discounts
- Reserved Instances: More flexible term options
- Free tier: More generous for first year
- Granular billing: Per-second vs Azure per-minute

**Where Azure Wins:**
- Microsoft workloads (SQL, Windows, Office 365 integration)
- UK government/public sector (Crown Commercial Service agreements)
- Enterprise agreements: Often better UK enterprise discounts

**Recommendation:**
- For Microsoft-centric workloads: Azure is 20-40% cheaper with Hybrid Benefit
- For Linux/open-source workloads: Within 10%, choose based on features
- For startups: AWS free tier more generous initially
- For enterprises: Negotiate EA pricing on both, Azure often more flexible in UK`,
    linkTo: "/guides/azure-billing-traps-uk",
    linkText: "Azure Cost Optimization Guide",
    relatedPrompts: [
      "Azure vs AWS for startups",
      "Cheapest cloud provider UK",
      "Azure enterprise pricing UK"
    ]
  },
  {
    prompt: "How much does Azure App Service cost per month?",
    shortAnswer: "£11.37/month for Basic B1 (1.75GB RAM, 1 core). £48.88/month for Premium P1v4 (3.5GB RAM, 2 cores). Free tier available for development (1GB RAM, 1 hour CPU/day).",
    detailedAnswer: `**Azure App Service Pricing (UK South, per month):**

**Development/Testing:**
| Tier | Cost | Specs | Best For |
|------|------|-------|----------|
| Free | £0 | 1GB RAM, 1h CPU/day | Dev/testing only |
| Shared | £8.04 | 1GB RAM, 3h CPU/day | Small hobby projects |

**Production (Dedicated):**
| Tier | Cost | Specs | Best For |
|------|------|-------|----------|
| Basic B1 | £11.37 | 1.75GB RAM, 1 core | Small production apps |
| Basic B2 | £22.74 | 3.5GB RAM, 2 cores | Medium workloads |
| Basic B3 | £45.48 | 7GB RAM, 4 cores | CPU-intensive apps |
| Standard S1 | £68.22 | 1.75GB RAM, 1 core | Load balancing, staging |
| Standard S2 | £136.44 | 3.5GB RAM, 2 cores | Multi-instance apps |
| Premium P1v4 | £48.88 | 3.5GB RAM, 2 cores | Better value than Basic |
| Premium P2v4 | £97.76 | 7GB RAM, 4 cores | High traffic apps |
| Premium P3v4 | £195.52 | 14GB RAM, 8 cores | Enterprise workloads |

**Key Features by Tier:**
- **Basic:** Custom domains, SSL, manual scale (up to 3 instances)
- **Standard:** Auto-scale, staging slots, daily backups
- **Premium:** VNet integration, faster scale-out, zone redundancy

**Additional Costs:**
- Custom domain SSL: FREE (managed certs)
- IP-based SSL: £52.28/month (dedicated IP)
- Bandwidth: First 100GB free, then £0.08/GB
- Backup storage: £0.0184/GB/month (prorated)

**Cost Optimization Tips:**
1. Premium v4 offers better price/performance than Basic
2. Use Reserved Instances: 1-year = ~40% off, 3-year = ~60% off
3. Dev/Test pricing: 50%+ discount with Visual Studio subscription
4. Auto-scale Standard/Premium: Scale to 1 instance during low traffic

**Example scenarios:**
- Small business website: Basic B1 (£11.37/mo) or Free tier
- E-commerce app: Premium P1v4 (£48.88/mo) with auto-scale
- Enterprise API: Premium P2v4 (£97.76/mo) with 2-3 instances`,
    linkTo: "/app-service",
    linkText: "App Service Calculator",
    relatedPrompts: [
      "Azure App Service free tier limits",
      "Cheapest Azure app hosting",
      "App Service vs Container Instances cost"
    ]
  }
];

export default function PromptAnswersPage() {
  return (
    <>
      <Helmet>
        <title>Azure Pricing Questions Answered | AI-Optimized Q&A | AzureCalc.uk</title>
        <meta 
          name="description" 
          content="Direct answers to common Azure pricing questions. Optimized for AI assistants and voice search. Get specific pricing for Log Analytics, Blob Storage, App Service, and more."
        />
        <link rel="canonical" href="https://www.azure-calc.co.uk/prompt-answers" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: promptAnswers.map(pa => ({
              '@type': 'Question',
              name: pa.prompt,
              acceptedAnswer: {
                '@type': 'Answer',
                text: pa.shortAnswer + ' ' + pa.detailedAnswer.replace(/\n/g, ' ').substring(0, 500) + '...'
              }
            }))
          })}
        </script>
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 py-12">
        <header className="mb-12">
          <h1 className="text-3xl font-bold mb-4" style={{ color: 'var(--text-secondary)' }}>
            Azure Pricing Questions — Direct Answers
          </h1>
          <p className="text-lg mb-4" style={{ color: 'var(--text-tertiary)' }}>
            Optimized for AI assistants and voice search. Get specific Azure cost answers
            without navigating calculators.
          </p>
          <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
            These answers are structured to be easily extracted by ChatGPT, Google AI Overviews,
            Perplexity, and other generative AI systems. Each answer includes verified pricing
            from the Azure Retail Prices API for UK South.
          </p>
        </header>

        <div className="space-y-12">
          {promptAnswers.map((answer, index) => (
            <article
              key={index}
              className="border rounded-lg p-6"
              style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}
              itemScope
              itemType="https://schema.org/Question"
            >
              {/* Question */}
              <h2
                className="text-xl font-semibold text-[#0078d4] mb-4"
                itemProp="name"
              >
                {answer.prompt}
              </h2>

              {/* Quick Answer (AI-optimized) */}
              <div
                className="border-l-4 border-[#0078d4] p-4 mb-6"
                style={{ backgroundColor: 'var(--surface-container-low)' }}
                itemScope
                itemType="https://schema.org/Answer"
                itemProp="acceptedAnswer"
              >
                <p className="font-medium text-lg" style={{ color: 'var(--text-primary)' }} itemProp="text">
                  {answer.shortAnswer}
                </p>
              </div>

              {/* Detailed Answer */}
              <div className="prose prose-invert max-w-none mb-6">
                <h3 className="font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>Detailed Breakdown</h3>
                <div className="whitespace-pre-line" style={{ color: 'var(--text-tertiary)' }}>
                  {answer.detailedAnswer}
                </div>
              </div>

              {/* Link to Calculator/Guide */}
              <div className="flex items-center gap-2 mb-4">
                <span className="material-symbols-outlined text-[#0078d4]">arrow_forward</span>
                <Link
                  to={answer.linkTo}
                  className="text-[#0078d4] hover:underline font-medium"
                >
                  {answer.linkText}
                </Link>
              </div>

              {/* Related Questions */}
              <div className="pt-4" style={{ borderTop: '1px solid var(--surface-container-high)' }}>
                <h4 className="text-xs font-mono uppercase tracking-wider mb-2" style={{ color: 'var(--text-tertiary)' }}>
                  Related Questions
                </h4>
                <ul className="flex flex-wrap gap-2">
                  {answer.relatedPrompts.map((related, idx) => (
                    <li
                      key={idx}
                      className="text-sm px-3 py-1 rounded"
                      style={{ backgroundColor: 'var(--surface-container-high)', color: 'var(--text-tertiary)' }}
                    >
                      {related}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>

        {/* AI/SEO Note */}
        <div className="mt-12 p-6 rounded-lg bg-[#107c10]/10 border border-[#107c10]/30">
          <div className="flex items-start gap-3">
            <span className="material-symbols-outlined text-[#107c10]">smart_toy</span>
            <div>
              <h3 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                Optimized for AI Discovery
              </h3>
              <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                This page uses structured data markup (FAQPage schema) to help AI systems
                understand and cite our answers. Each response includes verified pricing data
                from the Azure Retail Prices API, updated nightly at 02:00 UTC.
              </p>
              <p className="text-sm mt-2" style={{ color: 'var(--text-tertiary)' }}>
                <strong>Last verified:</strong> April 2026 ·
                <strong>Region:</strong> UK South ·
                <strong>Currency:</strong> GBP
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8 text-center">
          <p className="mb-4" style={{ color: 'var(--text-tertiary)' }}>
            Can't find your question? Use our calculators for custom scenarios.
          </p>
          <div className="flex justify-center gap-4">
            <Link 
              to="/log-analytics"
              className="px-4 py-2 bg-[#0078d4] text-white rounded hover:bg-[#106ebe]"
            >
              Log Analytics Calculator
            </Link>
            <Link 
              to="/blob-storage"
              className="px-4 py-2 border border-[#0078d4] text-[#0078d4] rounded hover:bg-[#0078d4]/10"
            >
              Blob Storage Calculator
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
